import { UserPlusIcon } from 'lucide-react'
import { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { ButtonLink } from 'slab'
import { useGetQuotingProspectsId } from 'utils/web_api'
import { type QuotingProspect } from 'utils/web_api.schemas'

import { AdminProspectInfo } from './AdminProspectInfo'
import { BrokerProspectInfo } from './BrokerProspectInfo'
import { CreateQuoteButton } from './CreateQuoteButton'
import { CreateQuoteFailed, GettingProspectFailed } from './GetErrorBanners'
import { ProspectEmployeeCensusList } from './ProspectEmployeeCensusList'
import { QuotesList, QuotesListV2 } from './QuotesList'
import { SkeletonItem } from '../reusable/SkeletonItem'

export function ProspectInfo ({
  isForAdminView,
  prospect,
  showEIN,
  showOwnerEmail,
}: {
  isForAdminView: boolean;
  prospect?: QuotingProspect | null;
  showEIN?: boolean;
  showOwnerEmail?: boolean;
}) {
  if (isForAdminView) {
    return (
      <AdminProspectInfo prospect={prospect} />
    )
  } else {
    return (
      <BrokerProspectInfo prospect={prospect} showEIN={showEIN} showOwnerEmail={showOwnerEmail} />
    )
  }
}

type Props = {
  isForAdminView: boolean;
  prospectId: string;
  showEIN?: boolean;
  showOwnerEmail?: boolean;
  quotingV2NewQuotePath?: string;
  quotingV2DynamicQuoteEditorPath?: string;
  quotingV2FlatQuoteEditorPath?: string;
  quotingV2QuoteList?: boolean;
}

function ProspectDetailsImpl ({
  isForAdminView = false,
  prospectId,
  showEIN = false,
  showOwnerEmail = false,
  quotingV2NewQuotePath,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
  quotingV2QuoteList = false,
}: Props) {
  const { data, error, isLoading } = useGetQuotingProspectsId(prospectId)
  const [createQuoteError, setCreateQuoteError] = useState<GrapeAPIErrorType | null>(null)

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  return (
    <div className='flex flex-col gap-y-10'>
      <div className='flex justify-between items-center'>
        <h1 className='heading-lg mb-2 pb-4'>
          {!isLoading && data ? data.name : <SkeletonItem />}
        </h1>
        {quotingV2NewQuotePath
          ? (
            <div className='flex flex-col gap-3 items-end'>
              <ButtonLink
                href={quotingV2NewQuotePath}
                variant='primary'
                leftIcon={UserPlusIcon}
              >
                Create new quote
              </ButtonLink>
              <CreateQuoteButton
                prospectId={prospectId}
                quotingV2Enabled
                onShowError={handleCreateQuoteError}
              />
            </div>)
          : (
            <CreateQuoteButton
              prospectId={prospectId}
              onShowError={handleCreateQuoteError}
            />
            )}
      </div>
      {createQuoteError != null && <CreateQuoteFailed errorObj={createQuoteError} />}
      <div>
        <h2 className='h2 font-normal pb-4'>Details</h2>
        {!isLoading && error != null && <GettingProspectFailed errorObj={error as GrapeAPIErrorType} />}
        {!isLoading && !error && data && (
          <ProspectInfo isForAdminView={isForAdminView} prospect={data} showEIN={showEIN || isForAdminView} showOwnerEmail={showOwnerEmail} />
        )}
      </div>
      <div>
        <h2 className='h2 font-normal pb-4'>Quotes</h2>
        {!quotingV2QuoteList && (
          <QuotesList
            isForAdminView={isForAdminView}
            prospectId={prospectId}
            quotingV2NewQuotePath={quotingV2NewQuotePath}
            quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
            quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
          />
        )}
        {quotingV2QuoteList && (
          <QuotesListV2
            isForAdminView={isForAdminView}
            prospectId={prospectId}
            quotingV2NewQuotePath={quotingV2NewQuotePath}
            quotingV2QuoteEditorPath={quotingV2DynamicQuoteEditorPath}
          />
        )}
      </div>
      {!quotingV2QuoteList && (
        <div>
          <h2 className='h2 font-normal pb-4'>Censuses</h2>
          <ProspectEmployeeCensusList
            isForAdminView={isForAdminView}
            prospectId={prospectId}
          />
        </div>
      )}
    </div>
  )
}

export default function ProspectDetails ({
  isForAdminView = false,
  prospectId,
  showEIN = false,
  showOwnerEmail = false,
  quotingV2NewQuotePath,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
  quotingV2QuoteList = false,
}: Props) {
  return (
    <ProspectDetailsImpl
      isForAdminView={isForAdminView}
      prospectId={prospectId}
      showEIN={showEIN}
      showOwnerEmail={showOwnerEmail}
      quotingV2NewQuotePath={quotingV2NewQuotePath}
      quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
      quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
      quotingV2QuoteList={quotingV2QuoteList}
    />
  )
}
